/**
 * Manual-link affordance (WS3, WI6). Admin roles only (super_admin /
 * administrator / office_admin); founder is blocked server-side by
 * blockFounderClientWrite, so this control is simply hidden for founders and
 * non-admins. Primary use: surname-change pairs (Albertolli, Prieto class) the
 * automatic matcher classifies as 'none'. Creates a PENDING match that the
 * reviewer then confirms under Needs Review with a relationship + outcome.
 */
import React, { useState } from 'react';
import { Link2, AlertCircle, CheckCircle } from 'lucide-react';
import type { RelationshipType } from '../../hooks/useRefinanceData';
import LoanSearchSelect from '../loans/LoanSearchSelect';
import type { LoanSearchResult } from '../../hooks/useLoanSearch';
import { useAuth } from '../../hooks/useAuth';
import { BROKER_FILTERED_ADMIN_COPY } from '../../utils/brokerFilteredAdmin';

interface ManualLinkControlProps {
  companyId: string;
  canManualLink: boolean;
  onCreate: (
    companyId: string,
    dischargedLoanId: string,
    newLoanId: string,
    notes?: string
  ) => Promise<{ matchId: string; daysBetween: number; suggestedRelationshipType: RelationshipType }>;
  /** When set, the discharged loan is fixed (Discharges per-row use): the
   *  discharged-ID input is hidden and this value is used. */
  presetDischargedLoanId?: string;
  /** Called after a successful link (e.g. so the host page can refetch). */
  onCreated?: () => void;
}

const ManualLinkControl: React.FC<ManualLinkControlProps> = ({
  companyId,
  canManualLink,
  onCreate,
  presetDischargedLoanId,
  onCreated,
}) => {
  // Creating a manual match is default-denied to a broker-restricted admin at
  // the callable chokepoint; disable the submit rather than let it 403.
  const { isBrokerFilteredAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [dischargedLoanId, setDischargedLoanId] = useState(presetDischargedLoanId ?? '');
  const [newLoan, setNewLoan] = useState<LoanSearchResult | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  if (!canManualLink) return null;

  const reset = () => {
    setDischargedLoanId(presetDischargedLoanId ?? '');
    setNewLoan(null);
    setNotes('');
  };

  // Picking (or clearing) a successor clears any stale outcome message. reset()
  // sets newLoan directly, so a success banner survives a completed submit.
  const handleNewLoanChange = (loan: LoanSearchResult | null) => {
    setNewLoan(loan);
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);
    const dl = dischargedLoanId.trim();
    const nl = newLoan?.loanId ?? '';
    if (!dl || !nl) {
      setError('Enter both the discharged loan ID and the new loan ID.');
      return;
    }
    if (dl === nl) {
      setError('The discharged and new loan IDs must be different.');
      return;
    }
    try {
      setSubmitting(true);
      const res = await onCreate(companyId, dl, nl, notes.trim() || undefined);
      const suggested = res.suggestedRelationshipType === 'returning_client' ? 'Returning Client' : 'Refinance';
      setSuccess(`Linked. A pending match was created (suggested: ${suggested}).`);
      reset();
      onCreated?.();
    } catch (err: any) {
      setError(err?.message || 'Could not create the manual link.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between p-4 text-left">
        <span className="flex items-center gap-2 text-sm font-medium text-gray-200">
          <Link2 className="w-4 h-4 text-cyan-400" />
          Link a loan manually
        </span>
        <span className="text-xs text-gray-500">
          {open ? 'Hide' : 'For surname changes the matcher cannot see'}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-gray-700 pt-4">
          <p className="text-xs text-gray-400">
            Creates a pending match between a discharged loan and its successor. Use this when a client
            returned under a changed surname so the automatic matcher missed the pair. The match lands in
            Needs Review for you to confirm as a refinance or a returning client.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {!presetDischargedLoanId && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">Discharged loan ID</label>
                <input
                  value={dischargedLoanId}
                  onChange={(e) => setDischargedLoanId(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-gray-100"
                  placeholder="e.g. 699387"
                />
              </div>
            )}
            <div>
              <LoanSearchSelect
                purpose="refinance_successor"
                value={newLoan}
                onChange={handleNewLoanChange}
                disabled={submitting}
                label="New (successor) loan"
                variant="manual"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Notes (optional)</label>
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
              className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-gray-100"
              placeholder="e.g. surname change confirmed by broker"
            />
          </div>
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-300">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 text-sm text-green-300">
              <CheckCircle className="w-4 h-4" />
              {success}
            </div>
          )}
          <div className="flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={isBrokerFilteredAdmin || submitting || !newLoan || !dischargedLoanId.trim()}
              title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : undefined}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
            >
              {submitting ? 'Linking...' : 'Create pending match'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualLinkControl;
